import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { createRetirableAuthStorage } from './authSessionStorage';
import { createEndingSessionFetch } from './endingSessionFetch';
import { createExpiredJwtReplayFetch } from './expiredJwtReplayFetch';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL ?? "";
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ?? "";

export { getSupabaseAuthStorageKey } from "./authStorageKey";
import { getSupabaseAuthStorageKey } from "./authStorageKey";

if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
  throw new Error(
    "Missing required Supabase environment variables: VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY must be set."
  );
}

const authStorageKey = getSupabaseAuthStorageKey(SUPABASE_URL);
const authSessionStorage = createRetirableAuthStorage(localStorage);

/** Terminal document teardown only; follow immediately with a hard auth navigation. */
export const stopAuthSessionPersistence = authSessionStorage.retire;

// Resolved the way supabase-js resolves its own PostgREST URL.
const REST_URL = new URL('rest/v1/', SUPABASE_URL.endsWith('/') ? SUPABASE_URL : `${SUPABASE_URL}/`).href;
const FUNCTIONS_URL = new URL('functions/v1/', SUPABASE_URL.endsWith('/') ? SUPABASE_URL : `${SUPABASE_URL}/`).href;

// ENT-6272: an expired-token PostgREST read refreshes once and replays (rule in
// expiredJwtReplayFetch.ts). The recovery lives with the Edge Function one in
// protectedFunctionAuth, which imports this module, so it is loaded on first
// use rather than at module evaluation.
const expiredJwtReplayFetch = createExpiredJwtReplayFetch({
  restUrl: REST_URL,
  recover: async (request) => {
    const { recoverExpiredRestRequest } = await import('@/lib/protectedFunctionAuth');
    return recoverExpiredRestRequest(request);
  },
});

// ENT-6474, ENT-6502: a PostgREST request or Edge Function call never leaves with a
// session being signed out (rule in endingSessionFetch.ts).
const endingSessionFetch = createEndingSessionFetch({
  fencedUrls: [REST_URL, FUNCTIONS_URL],
  currentAccessToken: async () => {
    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;
    return data.session?.access_token ?? null;
  },
  baseFetch: expiredJwtReplayFetch,
});

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";
export const supabase = createClient<Database>(
  SUPABASE_URL,
  SUPABASE_PUBLISHABLE_KEY,
  {
    auth: {
      storage: authSessionStorage,
      persistSession: true,
      autoRefreshToken: true,
      ...(authStorageKey ? { storageKey: authStorageKey } : {}),
    },
    global: {
      fetch: endingSessionFetch,
    },
  }
);
